import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Calendar, MapPin, Image, Plus, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const EventosGeneracionPage = () => {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEventos = async () => {
      try {
        const response = await axios.get(`${API}/api/eventos-generacion`);
        setEventos(response.data || []);
      } catch (error) {
        console.error("Error cargando eventos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEventos();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("es-MX", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-warm-bone to-slate-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-zuno-wine"
            data-testid="back-home"
          >
            <ArrowLeft className="mr-2" size={20} />
            Inicio
          </Button>
          <h1 className="text-xl font-bold text-zuno-wine">Eventos de la Generación</h1>
          <div className="w-24"></div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-zuno-wine mb-4">
            Nuestra Historia
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A lo largo de los años, hemos compartido momentos inolvidables. 
            Aquí recordamos cada uno de nuestros encuentros.
          </p>
        </div>

        {/* Eventos */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-12 w-12 border-4 border-zuno-wine border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando eventos...</p>
          </div>
        ) : eventos.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No hay eventos registrados aún</p>
          </div>
        ) : (
          <div className="space-y-8">
            {eventos.map((evento, index) => (
              <Card
                key={evento.id}
                className={`overflow-hidden ${evento.destacado ? 'ring-2 ring-zuno-wine' : ''}`}
                data-testid={`evento-card-${index}`}
              >
                <div className="md:flex">
                  {/* Imagen */}
                  <div className="md:w-1/3 h-64 md:h-auto relative">
                    {evento.imagen_url ? (
                      <img
                        src={evento.imagen_url}
                        alt={evento.titulo}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-zuno-wine to-slate-800 flex items-center justify-center">
                        <Calendar className="h-20 w-20 text-white/30" />
                      </div>
                    )}
                    {evento.destacado && (
                      <div className="absolute top-4 left-4 bg-zuno-wine text-white px-3 py-1 rounded-full text-sm font-semibold">
                        Próximo Evento
                      </div>
                    )}
                  </div>

                  {/* Contenido */}
                  <div className="md:w-2/3 p-6 md:p-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-zuno-wine mb-3">
                      {evento.titulo}
                    </h2>

                    <div className="flex flex-wrap gap-4 mb-4 text-slate-600">
                      <div className="flex items-center gap-2">
                        <Calendar size={18} className="text-zuno-wine" />
                        <span>{formatDate(evento.fecha)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin size={18} className="text-zuno-wine" />
                        <span>{evento.lugar}</span>
                      </div>
                    </div>

                    <p className="text-slate-700 whitespace-pre-line mb-6 line-clamp-4">
                      {evento.descripcion}
                    </p>

                    <div className="flex flex-wrap gap-3">
                      {evento.destacado && (
                        <Button
                          onClick={() => navigate("/comprar")}
                          className="btn-primary"
                          data-testid="comprar-boletos-btn"
                        >
                          Comprar Boletos
                          <ChevronRight size={18} className="ml-1" />
                        </Button>
                      )}
                      {evento.album_id && (
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/galeria?album=${evento.album_id}`)}
                          data-testid={`ver-fotos-${index}`}
                        >
                          <Image size={18} className="mr-2" />
                          Ver Fotos
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Timeline visual */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center text-zuno-wine mb-8">
            Línea del Tiempo
          </h2>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-zuno-wine/20"></div>
            {eventos.map((evento, index) => (
              <div
                key={evento.id}
                className={`relative flex items-center mb-8 ${
                  index % 2 === 0 ? 'justify-start' : 'justify-end'
                }`}
              >
                <div
                  className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}
                >
                  <div className="bg-white p-4 rounded-lg shadow-md inline-block">
                    <p className="text-sm text-zuno-wine font-semibold">
                      {new Date(evento.fecha + "T00:00:00").getFullYear()}
                    </p>
                    <p className="text-slate-800 font-medium">{evento.titulo}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-zuno-wine rounded-full border-4 border-white"></div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">
            Generación 1993-1996 • Preparatoria Uno
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EventosGeneracionPage;
